import type { ChatSSERagUsedEvent } from '../engine/events'
import type { FinalizedAttachment, MentionableItem, SendMessageOptions } from '../types'
import { logAmplitudeEvent } from '../utils'

/** The submit-time product event (unchanged payload — dashboards depend on it). */
export function trackSubmitMessage(params: {
  chatInput: string | undefined
  options: SendMessageOptions | undefined
  mentions: Array<MentionableItem>
  attachedContext: Array<FinalizedAttachment>
  submittedAttachmentsCount: number
  isNewChat: boolean
}): void {
  const { chatInput, options, mentions, attachedContext, submittedAttachmentsCount, isNewChat } = params
  logAmplitudeEvent('Submit Message', {
    messageLength: chatInput?.length ?? 0,
    voiceMode: options?.isPushToTalk ? ((options.pushToTalkMode ?? 'ask') as 'ask' | 'scribe') : undefined,
    attachments: options?.attachments?.length ?? submittedAttachmentsCount,
    attachmentTypes: attachedContext.map((ctx) => ctx.type),
    files: attachedContext.filter((ctx) => ctx.type === 'textFile' || ctx.type === 'pdf' || ctx.type === 'spreadsheet')
      .length,
    images: attachedContext.filter((ctx) => ctx.type === 'image').length,
    audioNotes: attachedContext.filter((ctx) => ctx.type === 'audioNote').length,
    clipboardTexts: attachedContext.filter((ctx) => ctx.type === 'clipboardText').length,
    selectedTexts: attachedContext.filter((ctx) => ctx.type === 'selectedText').length,
    summaries: attachedContext.filter((ctx) => ctx.type === 'dailySummary').length,
    highlights: attachedContext.filter((ctx) => ctx.type === 'insight').length,
    ocrs: attachedContext.filter((ctx) => ctx.type === 'ocr').length,
    windowContents: attachedContext.filter((ctx) => ctx.type === 'windowContents').length,
    mentions: mentions?.map((m) => m.type) ?? [],
    tool: mentions?.find((m) => m.type === 'backend_plugin')?.label ?? undefined,
    shortcut: mentions?.find((m) => m.type === 'shortcut')?.label ?? options?.prompt?.name ?? undefined,
    model: mentions?.find((m) => m.type === 'model')?.label ?? undefined,
    requestedCurrentContext: !!options?.requestCurrentContext,
    isPushToTalk: !!options?.isPushToTalk,
    isNewChat,
  })
}

/** RAG usage metrics for a turn's `rag-used` event (both the hit and miss shapes). */
export function trackRagUsed(event: ChatSSERagUsedEvent, conversationId: string, isNewChat: boolean): void {
  const chunks = event.chunks ?? []
  if (chunks.length === 0) {
    logAmplitudeEvent('RAG Search Used', {
      chunks_returned: 0,
      unique_documents: 0,
      document_names: [],
      average_relevance: 0,
      max_relevance: 0,
      min_relevance: 0,
      high_relevance_results: 0,
      total_text_length: 0,
      has_high_quality_results: false,
      conversation_id: conversationId,
      is_new_chat: isNewChat,
    })
    return
  }

  const relevanceScores = chunks.map((chunk) => chunk.score)
  const averageRelevance = relevanceScores.reduce((sum, score) => sum + score, 0) / relevanceScores.length
  const maxRelevance = Math.max(...relevanceScores)
  const minRelevance = Math.min(...relevanceScores)
  const documentNames = Array.from(new Set(chunks.map((chunk) => chunk.documentName)))
  const totalTextLength = chunks.reduce((sum, chunk) => sum + (chunk.text?.length || 0), 0)
  const highRelevanceResults = chunks.filter((chunk) => chunk.score >= 0.7).length

  logAmplitudeEvent('RAG Search Used', {
    chunks_returned: chunks.length,
    unique_documents: documentNames.length,
    document_names: documentNames,
    average_relevance: parseFloat(averageRelevance.toFixed(3)),
    max_relevance: parseFloat(maxRelevance.toFixed(3)),
    min_relevance: parseFloat(minRelevance.toFixed(3)),
    high_relevance_results: highRelevanceResults,
    total_text_length: totalTextLength,
    has_high_quality_results: maxRelevance >= 0.8,
    conversation_id: conversationId,
    is_new_chat: isNewChat,
  })
}
